import { Controller } from "@hotwired/stimulus";

// Search bar above the all-places map. Suggests cities, states and countries
// with MapKit autocomplete (MapKit is initialized by the dynamic map
// controller) and asks the map to move via "aon-map:center".
const CITY_SPAN = 0.5;
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 200;

export default class extends Controller {
  static targets = ["input", "message", "list", "destination"];
  static values = { country: String, destinationUrl: String };

  disconnect() {
    clearTimeout(this.debounceTimer);
  }

  suggest() {
    clearTimeout(this.debounceTimer);
    const query = this.inputTarget.value.trim();
    if (query.length < MIN_QUERY_LENGTH || !window.mapKitInitialized) {
      this._closeList();
      return;
    }

    this.debounceTimer = setTimeout(() => {
      const requestId = (this.requestId = (this.requestId || 0) + 1);
      this._search().autocomplete(query, (error, data) => {
        if (requestId !== this.requestId) return; // a newer keystroke won
        this._renderSuggestions(error ? [] : data.results);
      });
    }, DEBOUNCE_MS);
  }

  navigate(event) {
    const options = this.listTarget.querySelectorAll("[role=option]");

    switch (event.key) {
      case "ArrowDown":
      case "ArrowUp":
        if (options.length === 0) return;
        event.preventDefault();
        this._setActive(this.activeIndex + (event.key === "ArrowDown" ? 1 : -1), options);
        break;
      case "Enter":
        if (this.activeIndex >= 0 && options.length > 0) {
          event.preventDefault();
          this._choose(this.activeIndex);
        }
        break;
      case "Escape":
        this._closeList();
        break;
    }
  }

  // mousedown rather than click so it fires before the input's blur closes the list.
  pick(event) {
    event.preventDefault();
    this._choose(Number(event.currentTarget.dataset.index));
  }

  close() {
    this._closeList();
  }

  // Free-text submit (no suggestion chosen): geocode whatever was typed.
  search(event) {
    event.preventDefault();
    this._closeList();
    const query = this.inputTarget.value.trim();
    if (!query) return;

    if (!window.mapKitInitialized) {
      this._showMessage("The map is still loading. Try again in a moment.");
      return;
    }

    this.geocoder ||= new mapkit.Geocoder({ getsUserLocation: false });
    this.geocoder.lookup(query, (error, data) => {
      const result = !error && data.results[0];
      this._track("search_performed", {
        query,
        method: "free_text",
        found: !!result,
        matched: result ? result.formattedAddress : null,
      });
      if (!result) {
        this._showMessage(`Couldn't find "${query}". Try a city or country name.`);
        return;
      }
      this._goTo(result);
    });
  }

  nearMe() {
    this._closeList();
    this._hideDestination();
    if (!navigator.geolocation) {
      this._track("near_me_clicked", { outcome: "unsupported" });
      this._showMessage("Your browser doesn't support finding your location.");
      return;
    }

    this._showMessage("");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        this._track("near_me_clicked", { outcome: "located" });
        this._centerOn(position.coords, CITY_SPAN, CITY_SPAN);
      },
      (error) => this._showCountryInstead(error.code === error.PERMISSION_DENIED ? "denied" : "unavailable"),
      { timeout: 10000, maximumAge: 600000 }
    );
  }

  // When the browser won't give a location, frame the visitor's country
  // (from Cloudflare's CF-IPCountry) rather than leaving the map where it was.
  _showCountryInstead(outcome) {
    const reason =
      outcome === "denied" ? "Location access is off for this site" : "We couldn't find your location";
    const country = this._countryName();

    if (!country || !window.mapKitInitialized) {
      this._track("near_me_clicked", { outcome, fallback: "none" });
      this._showMessage(`${reason}. Search a city above to explore places there.`);
      return;
    }

    this._search().search(country, (error, data) => {
      const place = !error && data.places[0];
      this._track("near_me_clicked", { outcome, fallback: place ? "country" : "none" });
      if (!place) {
        this._showMessage(`${reason}. Search a city above to explore places there.`);
        return;
      }
      this._goTo(place, `${reason}, so here are places in ${country}. Search a city above to look somewhere else.`);
    });
  }

  _countryName() {
    // XX = unknown, T1 = Tor.
    if (!this.countryValue || ["XX", "T1"].includes(this.countryValue)) return null;
    try {
      return new Intl.DisplayNames(["en"], { type: "region" }).of(this.countryValue);
    } catch {
      return null;
    }
  }

  _search() {
    this.mapkitSearch ||= new mapkit.Search({
      getsUserLocation: false,
      includeAddresses: true,
      includePointsOfInterest: false,
      includeQueries: false,
      includePhysicalFeatures: false,
      addressFilter: mapkit.AddressFilter.including([
        mapkit.AddressCategory.Locality,
        mapkit.AddressCategory.AdministrativeArea,
        mapkit.AddressCategory.Country,
      ]),
    });
    return this.mapkitSearch;
  }

  _renderSuggestions(results) {
    this.suggestions = results.slice(0, 8);
    this.listTarget.innerHTML = "";
    this.activeIndex = -1;

    this.suggestions.forEach((result, index) => {
      const [primary, secondary] = result.displayLines;
      const option = document.createElement("li");
      option.id = `all-places-suggestion-${index}`;
      option.setAttribute("role", "option");
      option.setAttribute("aria-selected", "false");
      option.dataset.index = index;
      option.dataset.action = "mousedown->all-places-search#pick";
      option.className = "cursor-pointer px-4 py-2 text-left hover:bg-gray-100 aria-selected:bg-gray-100";

      const name = document.createElement("span");
      name.className = "block font-semibold text-gray-900";
      name.textContent = primary;
      option.append(name);

      if (secondary) {
        const region = document.createElement("span");
        region.className = "block text-sm text-gray-600";
        region.textContent = secondary;
        option.append(region);
      }

      this.listTarget.append(option);
    });

    const open = this.suggestions.length > 0;
    this.listTarget.classList.toggle("hidden", !open);
    this.inputTarget.setAttribute("aria-expanded", String(open));
    this.inputTarget.removeAttribute("aria-activedescendant");
  }

  _setActive(index, options) {
    this.activeIndex = (index + options.length) % options.length;
    options.forEach((option, i) => option.setAttribute("aria-selected", String(i === this.activeIndex)));
    this.inputTarget.setAttribute("aria-activedescendant", options[this.activeIndex].id);
  }

  _choose(index) {
    const suggestion = this.suggestions[index];
    if (!suggestion) return;

    this.inputTarget.value = suggestion.displayLines.filter(Boolean).join(", ");
    this._closeList();
    this._track("search_performed", {
      query: this.inputTarget.value,
      method: "suggestion",
      found: true,
      matched: this.inputTarget.value,
    });

    // Autocomplete results only carry a point; a full search on the
    // suggestion returns the place's region so countries frame properly.
    this._search().search(suggestion, (error, data) => {
      const place = !error && data.places[0];
      this._goTo(place || { formattedAddress: this.inputTarget.value, coordinate: suggestion.coordinate });
    });
  }

  _goTo(result, message = `Showing ${result.formattedAddress}`) {
    // MapKit fuzzy-matches almost anything, so say what it matched.
    this._showMessage(message);
    this._linkDestination(result);
    if (result.region) {
      this._centerOn(result.region.center, result.region.span.latitudeDelta, result.region.span.longitudeDelta);
    } else {
      this._centerOn(result.coordinate, CITY_SPAN, CITY_SPAN);
    }
  }

  // Offer our destination page for wherever the map went (the city's, else
  // its state's or country's), so searchers have somewhere to go next.
  _linkDestination(place) {
    const lookupId = this._hideDestination();
    if (!place.countryCode) return;

    const link = this.destinationTarget;
    const params = new URLSearchParams({ country_code: place.countryCode });
    if (place.locality) params.set("city", place.locality);
    if (place.administrativeArea) params.set("state", place.administrativeArea);

    fetch(`${this.destinationUrlValue}?${params}`, { headers: { Accept: "application/json" } })
      .then((response) => (response.ok ? response.json() : {}))
      .then((destination) => {
        if (lookupId !== this.lookupId || !destination.url) return;
        link.href = destination.url;
        link.textContent = `Explore ${destination.name} →`;
        link.dataset.level = destination.level;
        link.classList.remove("hidden");
        link.classList.add("inline-block");
      })
      .catch(() => {});
  }

  // Also returns a new lookup id, so a lookup still in flight can't re-show it.
  _hideDestination() {
    this.destinationTarget.classList.add("hidden");
    this.destinationTarget.classList.remove("inline-block");
    return (this.lookupId = (this.lookupId || 0) + 1);
  }

  trackDestination() {
    window.posthog?.capture?.(
      "map_destination_clicked",
      { source: "all_places_map", level: this.destinationTarget.dataset.level, url: this.destinationTarget.getAttribute("href") },
      { transport: "sendBeacon" }
    );
  }

  _centerOn({ latitude, longitude }, latitudeDelta, longitudeDelta) {
    window.dispatchEvent(
      new CustomEvent("aon-map:center", { detail: { latitude, longitude, latitudeDelta, longitudeDelta } })
    );
  }

  _closeList() {
    this.listTarget.classList.add("hidden");
    this.listTarget.innerHTML = "";
    this.suggestions = [];
    this.activeIndex = -1;
    this.inputTarget.setAttribute("aria-expanded", "false");
    this.inputTarget.removeAttribute("aria-activedescendant");
  }

  _track(event, properties) {
    window.posthog?.capture?.(event, { source: "all_places_map", ...properties });
  }

  _showMessage(text) {
    this.messageTarget.textContent = text;
    this.messageTarget.classList.toggle("hidden", !text);
  }
}
